import { useState } from "react";
import { Scale, LogIn, User2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

function formatError(d) {
  if (!d) return "Giriş yapılamadı";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function LoginPage() {
  const { login } = useAuth();
  const saved = (() => { try { return JSON.parse(localStorage.getItem("uyap_saved") || "{}"); } catch { return {}; } })();
  const [username, setUsername] = useState(saved.username || "");
  const [phone, setPhone] = useState(saved.phone || "");
  const [remember, setRemember] = useState(Boolean(saved.username));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(username.trim(), phone.trim());
      if (remember) {
        localStorage.setItem("uyap_saved", JSON.stringify({ username: username.trim(), phone: phone.trim() }));
      } else {
        localStorage.removeItem("uyap_saved");
      }
    } catch (ex) {
      setErr(formatError(ex?.response?.data?.detail) || "Giriş yapılamadı");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--uyap-bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-lg bg-amber-600/10 border border-amber-600/40 flex items-center justify-center">
            <Scale className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <div className="font-serif text-2xl font-bold text-amber-500">UYAP</div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Avukat Portal</div>
          </div>
        </div>

        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-8 shadow-2xl">
          <h1 className="font-serif text-3xl font-bold text-slate-100">Giriş Yap</h1>
          <p className="text-sm text-slate-400 mt-2">Dava & Finans Yönetim Sistemine hoş geldiniz</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-slate-400 flex items-center gap-1.5"><User2 className="w-3 h-3" /> Kullanıcı Adı</Label>
              <Input
                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                className="bg-slate-800 border-slate-700 mt-1 text-slate-100"
                placeholder="Ugu" required autoFocus autoComplete="username"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Cep Telefonu</Label>
              <Input
                type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="bg-slate-800 border-slate-700 mt-1 text-slate-100 font-mono"
                placeholder="+90 5XX XXX XX XX" required autoComplete="tel"
                data-testid="login-phone-input"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))}
                className="border-slate-600 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                data-testid="login-remember-checkbox" />
              <Label htmlFor="remember" className="text-sm text-slate-300 cursor-pointer">
                Beni hatırla (bu cihazda)
              </Label>
            </div>
            {err && (
              <div className="text-sm text-red-400 bg-red-950/40 border border-red-900/60 rounded px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit" disabled={busy}
              className="w-full bg-amber-600 hover:bg-amber-700 text-slate-950 font-semibold h-11"
              data-testid="login-submit-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
